import { motion } from "framer-motion";
import { Phone, Star, ShieldCheck, Clock, Wrench } from "lucide-react";
import { COMPANY } from "@/data/site";
import QuoteForm from "./QuoteForm";

// Split the H1 into word tokens so we can mask-reveal them line-by-line.
const HERO_LINES = [
  ["Where", "others"],
  ["stop", "at", "the", "base,"],
  ["we", "strive", "for", "the", "peak."],
];

const wordVariants = {
  hidden: { y: "110%", opacity: 0 },
  visible: (i) => ({
    y: 0,
    opacity: 1,
    transition: {
      delay: 0.15 + i * 0.045,
      duration: 0.85,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

export default function Hero() {
  let wordIdx = -1;
  return (
    <section id="top" className="relative bg-white overflow-hidden">
      {/* Signature typographic backdrop — very subtle parallax feel */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 0.06, scale: 1 }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none absolute right-[-8%] top-[-4%] hidden md:block font-display font-black text-[#1E3A5F] leading-[0.82] text-[16rem] tracking-tight select-none"
      >
        PEAK.
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-full"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-xs font-semibold tracking-wide text-slate-700">
              Open now · {COMPANY.region}
            </span>
          </motion.div>

          {/* Masked line-by-line H1 reveal */}
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-[#1E3A5F] mt-5 leading-[0.95]">
            {HERO_LINES.map((line, lineIdx) => {
              const isAccent = lineIdx === 2;
              return (
                <span key={lineIdx} className="block overflow-hidden pb-1">
                  <span className="inline-flex flex-wrap gap-x-3">
                    {line.map((word) => {
                      wordIdx += 1;
                      return (
                        <motion.span
                          key={`${lineIdx}-${wordIdx}`}
                          custom={wordIdx}
                          variants={wordVariants}
                          initial="hidden"
                          animate="visible"
                          className={
                            "inline-block " + (isAccent ? "text-[#A88852]" : "")
                          }
                        >
                          {word}
                        </motion.span>
                      );
                    })}
                  </span>
                </span>
              );
            })}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="text-slate-600 text-lg md:text-xl mt-6 max-w-xl leading-relaxed"
          >
            <strong className="text-[#1E3A5F]">Peak Plumbing</strong> — Macomb County's family-owned drain, sewer &amp; plumbing experts. Fully insured, honest pricing, and dependable service.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.05 }}
            className="flex flex-wrap items-center gap-3 mt-7"
          >
            <a
              data-testid="hero-call-cta"
              href={`tel:${COMPANY.phoneTel}`}
              className="btn-cta inline-flex items-center gap-2.5 px-6 py-3.5 text-base"
            >
              <Phone size={18} /> Call {COMPANY.phone}
            </a>
            <a
              data-testid="hero-quote-cta"
              href="#quote"
              className="btn-outline-dark inline-flex items-center px-6 py-3.5 text-base"
            >
              Schedule an Appointment
            </a>
          </motion.div>

          {/* Trust bar */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.08, delayChildren: 1.2 } } }}
            className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl"
          >
            {[
              { icon: Star, label: `${COMPANY.rating} / 5`, sub: `${COMPANY.reviewCount}+ reviews` },
              { icon: ShieldCheck, label: "Insured", sub: "For your peace of mind" },
              { icon: Clock, label: "Fast", sub: "Same-day dispatch" },
              { icon: Wrench, label: "Family owned", sub: "Local & independent" },
            ].map((t, i) => (
              <motion.div
                key={i}
                data-testid={`hero-trust-${i}`}
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
                }}
                className="bg-white/80 backdrop-blur border border-slate-200 rounded-md px-4 py-3"
              >
                <div className="flex items-center gap-2">
                  <t.icon size={16} className="text-orange-500" />
                  <span className="font-display font-bold text-slate-900 text-base">{t.label}</span>
                </div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{t.sub}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Right: quote form */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          id="quote"
          className="lg:col-span-5"
        >
          <QuoteForm />
        </motion.div>
      </div>

      {/* Editorial marquee — one, slow, deliberate */}
      <div className="relative border-y border-slate-200 bg-[#F5EFE3] overflow-hidden">
        <motion.div
          initial={{ x: 0 }}
          animate={{ x: "-50%" }}
          transition={{ duration: 42, ease: "linear", repeat: Infinity }}
          className="whitespace-nowrap flex gap-16 py-6"
          aria-hidden
        >
          {Array.from({ length: 2 }).map((_, dup) => (
            <div key={dup} className="flex items-center gap-16 pr-16 shrink-0">
              {["Drain cleaning", "Sewer repair", "Water heaters", "Leak detection", "Sump pumps", "Fixture repair", "Water treatment", "Emergency"].map((t, i) => (
                <span
                  key={`${dup}-${i}`}
                  className="font-display font-black text-4xl tracking-tight text-[#1E3A5F] flex items-center gap-16"
                >
                  {t}
                  <span className="w-1.5 h-1.5 rounded-full bg-[#A88852] shrink-0" />
                </span>
              ))}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
